import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { TabType } from '../types';
import { 
  Calendar, 
  FolderGit2, 
  CheckSquare, 
  BookOpen, 
  ExternalLink 
} from 'lucide-react';

export const Header: React.FC = () => {
  const { activeTab, setActiveTab, settings } = useApp();
  const [showNoProgramModal, setShowNoProgramModal] = useState(false);

  const navItems: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'schedule', label: 'Schedule', icon: <Calendar className="w-4 h-4" /> },
    { id: 'projects', label: 'Ministries', icon: <FolderGit2 className="w-4 h-4" /> },
    {
      id: 'vote',
      label: 'Vote',
      icon: <CheckSquare className="w-4 h-4" />,
    },
  ];

  return (
    <>
      {/* Top Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-brand-tan shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Brand / Logo */}
            <div 
              className="flex items-center gap-3 cursor-pointer group"
              onClick={() => setActiveTab('projects')}
            >
              <img 
                src="/exponential-logo.png" 
                alt="Exponential" 
                className="h-6 sm:h-7 w-auto object-contain transition-transform group-hover:scale-105"
              />
              <div className="h-5 sm:h-6 w-px bg-brand-tan hidden sm:block self-center" />
              <div className="flex items-center">
                <span className="font-display font-extrabold text-base sm:text-lg tracking-tight text-brand-black leading-none">
                  Shark Tank
                </span>
              </div>
            </div>

            {/* Mobile Top Header Action: Digital Program Book */}
            <div className="flex md:hidden items-center">
              {settings.programBookUrl ? (
                <a
                  href={settings.programBookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-tan/20 hover:bg-brand-tan/40 border border-brand-tan text-brand-black text-xs font-bold transition-all shadow-2xs"
                >
                  <BookOpen className="w-3.5 h-3.5 text-brand-red" />
                  <span>Program</span>
                  <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNoProgramModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-tan/20 hover:bg-brand-tan/40 border border-brand-tan text-brand-black text-xs font-bold transition-all shadow-2xs"
                >
                  <BookOpen className="w-3.5 h-3.5 text-brand-red" />
                  <span>Program</span>
                </button>
              )}
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`relative flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-brand-red/10 text-brand-red font-bold border border-brand-red/30'
                        : 'text-brand-subtext hover:text-brand-black hover:bg-brand-offwhite'
                    }`}
                  >
                    <span className={isActive ? 'text-brand-red' : ''}>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                );
              })}

              {/* Digital Program Book Nav Option */}
              {settings.programBookUrl ? (
                <a
                  href={settings.programBookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-brand-subtext hover:text-brand-black hover:bg-brand-offwhite transition-all group"
                  title="Open Digital Program Book in a new tab"
                >
                  <BookOpen className="w-4 h-4 text-brand-red group-hover:scale-110 transition-transform" />
                  <span>Program Book</span>
                  <ExternalLink className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNoProgramModal(true)}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-brand-subtext hover:text-brand-black hover:bg-brand-offwhite transition-all group"
                  title="Digital Program Book"
                >
                  <BookOpen className="w-4 h-4 text-brand-red group-hover:scale-110 transition-transform" />
                  <span>Program Book</span>
                </button>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Modal when no program book URL configured */}
      {showNoProgramModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-white border border-brand-tan rounded-3xl p-6 shadow-2xl text-center space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-brand-red/10 border border-brand-red/20 mx-auto flex items-center justify-center text-brand-red">
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-extrabold text-brand-black text-base">Digital Program Book</h3>
              <p className="text-xs text-brand-subtext mt-1.5 leading-relaxed">
                The digital program book link has not been added yet by the event organizer. Please check back shortly or configure the URL in the Admin section.
              </p>
            </div>
            <div className="flex justify-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowNoProgramModal(false)}
                className="px-5 py-2 bg-brand-red hover:bg-red-600 text-white text-xs font-bold rounded-xl shadow-sm transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation Bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-brand-tan px-2 py-1.5 pb-safe shadow-lg">
        <div className="flex items-center justify-around">
          {navItems.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`relative flex flex-col items-center justify-center py-1 px-2.5 rounded-lg transition-all ${
                  isActive ? 'text-brand-red font-bold' : 'text-brand-subtext hover:text-brand-black'
                }`}
              >
                <div>
                  {item.icon}
                </div>
                <span className="text-[10px] font-medium mt-1 tracking-tight">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};
